import axios from 'axios';
import { API_URL } from './api';

/**
 * Клиент рекламного кабинета партнёра.
 *
 * ВАЖНО про префикс: partners.urls подключены в корневом urlconf на '/partner/',
 * а рекламный роутер — через include внутри них. Поэтому база именно
 * '/partner/ads', без '/api'. Сверяйся с api_partner.js, а не с документацией.
 */
export const ADS_API_BASE = `${API_URL}/api/business/ads`;

const _err = (error) => {
    if (error.response) {
        console.error('Ads API error:', error.response.status, error.response.data);
    } else {
        console.error('Ads API error:', error.message);
    }
};

const auth = (token) => ({
    headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
    },
});

// Понадобится на Ф2 для загрузки креативов. Content-Type не задаём —
// axios сам проставит boundary для FormData.
export const authMultipart = (token) => ({
    headers: { Authorization: `Bearer ${token}` },
});

/** Достаёт человекочитаемый текст ошибки из ответа DRF. */
export const adsErrorText = (e, fallback = 'Не удалось выполнить операцию') => {
    const data = e?.response?.data;
    if (!data) return fallback;
    if (typeof data === 'string') return data;
    if (data.error) return data.error;
    if (Array.isArray(data.errors)) return data.errors.join('\n');
    if (data.detail) return data.detail;
    const firstField = Object.keys(data)[0];
    if (firstField) {
        const value = data[firstField];
        return Array.isArray(value) ? `${firstField}: ${value[0]}` : String(value);
    }
    return fallback;
};


// ===================== АККАУНТ И СПРАВОЧНИКИ =====================

export const APIAdsAccount = async (token) => {
    try {
        const r = await axios.get(`${ADS_API_BASE}/account/`, auth(token));
        return r.data; // { success, account, limits }
    } catch (e) { _err(e); throw e; }
};

export const APIAdsAccountUpdate = async (token, data) => {
    try {
        const r = await axios.patch(`${ADS_API_BASE}/account/update/`, data, auth(token));
        return r.data;
    } catch (e) { _err(e); throw e; }
};


// ===================== БИЛЛИНГ =====================

export const APIAdsBillingSummary = async (token, days = 30) => {
    try {
        const r = await axios.get(`${ADS_API_BASE}/billing/summary/?days=${days}`, auth(token));
        return r.data;
    } catch (e) { _err(e); throw e; }
};

export const APIAdsBillingSettings = async (token, lowBalanceThreshold) => {
    try {
        const r = await axios.patch(
            `${ADS_API_BASE}/billing/settings/`,
            { low_balance_threshold: lowBalanceThreshold },
            auth(token),
        );
        return r.data;
    } catch (e) { _err(e); throw e; }
};

export const APIAdsDeposits = async (token, { status, page = 1, pageSize = 20 } = {}) => {
    try {
        const params = new URLSearchParams();
        if (status) params.append('status', status);
        params.append('page', page);
        params.append('page_size', pageSize);
        const r = await axios.get(`${ADS_API_BASE}/billing/deposits/?${params}`, auth(token));
        return r.data;
    } catch (e) { _err(e); throw e; }
};

export const APIAdsDepositCreate = async (token, { amount, payerEmail, payerPhone }) => {
    try {
        const payload = { amount };
        if (payerEmail) payload.payer_email = payerEmail;
        if (payerPhone) payload.payer_phone = payerPhone;
        const r = await axios.post(`${ADS_API_BASE}/billing/deposits/create/`, payload, auth(token));
        return r.data; // { success, deposit, payment_url }
    } catch (e) { _err(e); throw e; }
};

/**
 * Статус пополнения. При pending сервер сам сверяется с ЮKassa и зачисляет
 * деньги, поэтому этот вызов используется как опрос после возврата с оплаты.
 */
export const APIAdsDepositDetail = async (token, depositId) => {
    try {
        const r = await axios.get(`${ADS_API_BASE}/billing/deposits/${depositId}/`, auth(token));
        return r.data; // { success, deposit }
    } catch (e) { _err(e); throw e; }
};

export const APIAdsTransactions = async (token, { txType, page = 1, pageSize = 30 } = {}) => {
    try {
        const params = new URLSearchParams();
        if (txType) params.append('tx_type', txType);
        params.append('page', page);
        params.append('page_size', pageSize);
        const r = await axios.get(`${ADS_API_BASE}/account/transactions/?${params}`, auth(token));
        return r.data;
    } catch (e) { _err(e); throw e; }
};

/**
 * Выгрузка детализации. Ходим через axios с responseType blob:
 * эндпоинт требует заголовок Authorization, а обычная ссылка его не пошлёт.
 */
export const APIAdsStatementDownload = async (token, { dateFrom, dateTo } = {}) => {
    try {
        const params = new URLSearchParams();
        if (dateFrom) params.append('date_from', dateFrom);
        if (dateTo) params.append('date_to', dateTo);
        const r = await axios.get(`${ADS_API_BASE}/billing/statement/?${params}`, {
            headers: { Authorization: `Bearer ${token}` },
            responseType: 'blob',
        });
        return r.data;
    } catch (e) { _err(e); throw e; }
};

// ===================== ОТЧЁТЫ =====================

/**
 * Общие параметры всех отчётов: либо period, либо пара дат.
 * campaign_id сужает отчёт до одной кампании.
 */
const reportParams = ({ period, dateFrom, dateTo, campaignId, ...rest } = {}) => {
    const params = new URLSearchParams();
    if (dateFrom || dateTo) {
        if (dateFrom) params.append('date_from', dateFrom);
        if (dateTo) params.append('date_to', dateTo);
    } else if (period) {
        params.append('period', period);
    }
    if (campaignId) params.append('campaign_id', campaignId);
    Object.entries(rest).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') params.append(key, value);
    });
    return params.toString();
};

export const APIAdsReportSummary = async (token, options) => {
    try {
        const r = await axios.get(
            `${ADS_API_BASE}/reports/summary/?${reportParams(options)}`, auth(token)
        );
        return r.data;
    } catch (e) { _err(e); throw e; }
};

export const APIAdsReportTimeline = async (token, options) => {
    try {
        const r = await axios.get(
            `${ADS_API_BASE}/reports/timeline/?${reportParams(options)}`, auth(token)
        );
        return r.data;
    } catch (e) { _err(e); throw e; }
};

export const APIAdsReportCampaigns = async (token, options) => {
    try {
        const r = await axios.get(
            `${ADS_API_BASE}/reports/campaigns/?${reportParams(options)}`, auth(token)
        );
        return r.data;
    } catch (e) { _err(e); throw e; }
};

export const APIAdsReportCreatives = async (token, options) => {
    try {
        const r = await axios.get(
            `${ADS_API_BASE}/reports/creatives/?${reportParams(options)}`, auth(token)
        );
        return r.data;
    } catch (e) { _err(e); throw e; }
};

export const APIAdsReportPlacements = async (token, options) => {
    try {
        const r = await axios.get(
            `${ADS_API_BASE}/reports/placements/?${reportParams(options)}`, auth(token)
        );
        return r.data;
    } catch (e) { _err(e); throw e; }
};

export const APIAdsReportGeo = async (token, options) => {
    try {
        const r = await axios.get(
            `${ADS_API_BASE}/reports/geo/?${reportParams(options)}`, auth(token)
        );
        return r.data;
    } catch (e) { _err(e); throw e; }
};

export const APIAdsReportAudience = async (token, options) => {
    try {
        const r = await axios.get(
            `${ADS_API_BASE}/reports/audience/?${reportParams(options)}`, auth(token)
        );
        return r.data;
    } catch (e) { _err(e); throw e; }
};

export const APIAdsCampaignStats = async (token, campaignId, options) => {
    try {
        const r = await axios.get(
            `${ADS_API_BASE}/campaigns/${campaignId}/stats/?${reportParams(options)}`,
            auth(token),
        );
        return r.data;
    } catch (e) { _err(e); throw e; }
};

export const APIAdsReportExport = async (token, report, options) => {
    try {
        const r = await axios.get(
            `${ADS_API_BASE}/reports/export/?report=${report}&${reportParams(options)}`,
            { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' },
        );
        return r.data;
    } catch (e) { _err(e); throw e; }
};


/** Сохраняет полученный blob как файл. */
export const saveBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    // Освобождаем адрес не сразу: Safari успевает отменить скачивание
    setTimeout(() => URL.revokeObjectURL(url), 1000);
};

export const APIAdsPlacements = async (token) => {
    try {
        const r = await axios.get(`${ADS_API_BASE}/placements/`, auth(token));
        return r.data; // { success, items }
    } catch (e) { _err(e); throw e; }
};

export const APIAdsDictionaries = async (token) => {
    try {
        const r = await axios.get(`${ADS_API_BASE}/dictionaries/`, auth(token));
        return r.data;
    } catch (e) { _err(e); throw e; }
};


// ===================== КАМПАНИИ =====================

export const APIAdsCampaigns = async (token, {
    status, search, pricingModel, hideArchived, orderBy, page = 1, pageSize = 20,
} = {}) => {
    try {
        const params = new URLSearchParams();
        if (status) params.append('status', Array.isArray(status) ? status.join(',') : status);
        if (search) params.append('search', search);
        if (pricingModel) params.append('pricing_model', pricingModel);
        if (hideArchived) params.append('hide_archived', 'true');
        if (orderBy) params.append('order_by', orderBy);
        params.append('page', page);
        params.append('page_size', pageSize);
        const r = await axios.get(`${ADS_API_BASE}/campaigns/?${params}`, auth(token));
        return r.data; // { success, items, total, page, pages, counters }
    } catch (e) { _err(e); throw e; }
};

export const APIAdsCampaignCreate = async (token, data) => {
    try {
        const r = await axios.post(`${ADS_API_BASE}/campaigns/create/`, data, auth(token));
        return r.data; // { success, campaign }
    } catch (e) { _err(e); throw e; }
};

export const APIAdsCampaignDetail = async (token, campaignId) => {
    try {
        const r = await axios.get(`${ADS_API_BASE}/campaigns/${campaignId}/`, auth(token));
        return r.data; // { success, campaign }
    } catch (e) { _err(e); throw e; }
};

export const APIAdsCampaignUpdate = async (token, campaignId, data) => {
    try {
        const r = await axios.patch(
            `${ADS_API_BASE}/campaigns/${campaignId}/update/`, data, auth(token)
        );
        return r.data;
    } catch (e) { _err(e); throw e; }
};

export const APIAdsCampaignDelete = async (token, campaignId) => {
    try {
        const r = await axios.delete(`${ADS_API_BASE}/campaigns/${campaignId}/delete/`, auth(token));
        return r.data;
    } catch (e) { _err(e); throw e; }
};

export const APIAdsCampaignSubmit = async (token, campaignId) => {
    try {
        const r = await axios.post(
            `${ADS_API_BASE}/campaigns/${campaignId}/submit/`, {}, auth(token)
        );
        return r.data; // { success, auto_approved, campaign }
    } catch (e) { _err(e); throw e; }
};

export const APIAdsCampaignPause = async (token, campaignId) => {
    try {
        const r = await axios.post(
            `${ADS_API_BASE}/campaigns/${campaignId}/pause/`, {}, auth(token)
        );
        return r.data;
    } catch (e) { _err(e); throw e; }
};

export const APIAdsCampaignResume = async (token, campaignId) => {
    try {
        const r = await axios.post(
            `${ADS_API_BASE}/campaigns/${campaignId}/resume/`, {}, auth(token)
        );
        return r.data;
    } catch (e) { _err(e); throw e; }
};

export const APIAdsCampaignArchive = async (token, campaignId) => {
    try {
        const r = await axios.post(
            `${ADS_API_BASE}/campaigns/${campaignId}/archive/`, {}, auth(token)
        );
        return r.data;
    } catch (e) { _err(e); throw e; }
};

export const APIAdsCampaignModerationLog = async (token, campaignId) => {
    try {
        const r = await axios.get(
            `${ADS_API_BASE}/campaigns/${campaignId}/moderation-log/`, auth(token)
        );
        return r.data; // { success, items }
    } catch (e) { _err(e); throw e; }
};


// ===================== ГРУППЫ И КРЕАТИВЫ (нужны с Ф2) =====================

export const APIAdsGroups = async (token, campaignId) => {
    try {
        const r = await axios.get(
            `${ADS_API_BASE}/campaigns/${campaignId}/groups/`, auth(token)
        );
        return r.data;
    } catch (e) { _err(e); throw e; }
};

export const APIAdsGroupCreate = async (token, campaignId, data) => {
    try {
        const r = await axios.post(
            `${ADS_API_BASE}/campaigns/${campaignId}/groups/create/`, data, auth(token)
        );
        return r.data;
    } catch (e) { _err(e); throw e; }
};

export const APIAdsGroupUpdate = async (token, groupId, data) => {
    try {
        const r = await axios.patch(`${ADS_API_BASE}/groups/${groupId}/update/`, data, auth(token));
        return r.data;
    } catch (e) { _err(e); throw e; }
};

export const APIAdsGroupDelete = async (token, groupId) => {
    try {
        const r = await axios.delete(`${ADS_API_BASE}/groups/${groupId}/delete/`, auth(token));
        return r.data;
    } catch (e) { _err(e); throw e; }
};

export const APIAdsTargetingGet = async (token, groupId) => {
    try {
        const r = await axios.get(`${ADS_API_BASE}/groups/${groupId}/targeting/`, auth(token));
        return r.data;
    } catch (e) { _err(e); throw e; }
};

export const APIAdsTargetingUpdate = async (token, groupId, data) => {
    try {
        const r = await axios.put(`${ADS_API_BASE}/groups/${groupId}/targeting/`, data, auth(token));
        return r.data;
    } catch (e) { _err(e); throw e; }
};

export const APIAdsCreatives = async (token, groupId) => {
    try {
        const r = await axios.get(`${ADS_API_BASE}/groups/${groupId}/creatives/`, auth(token));
        return r.data;
    } catch (e) { _err(e); throw e; }
};

export const APIAdsCreativeCreate = async (token, groupId, formData) => {
    try {
        const r = await axios.post(
            `${ADS_API_BASE}/groups/${groupId}/creatives/create/`,
            formData, authMultipart(token)
        );
        return r.data;
    } catch (e) { _err(e); throw e; }
};

export const APIAdsCreativeUpdate = async (token, creativeId, formData) => {
    try {
        const isForm = typeof FormData !== 'undefined' && formData instanceof FormData;
        const r = await axios.patch(
            `${ADS_API_BASE}/creatives/${creativeId}/update/`,
            formData,
            isForm ? authMultipart(token) : auth(token),
        );
        return r.data;
    } catch (e) { _err(e); throw e; }
};

export const APIAdsCreativeDelete = async (token, creativeId) => {
    try {
        const r = await axios.delete(`${ADS_API_BASE}/creatives/${creativeId}/delete/`, auth(token));
        return r.data;
    } catch (e) { _err(e); throw e; }
};


// ===================== КОРРЕКТИРОВКИ СТАВОК =====================

export const APIAdsAdjustments = async (token, campaignId) => {
    try {
        const r = await axios.get(
            `${ADS_API_BASE}/campaigns/${campaignId}/adjustments/`, auth(token)
        );
        return r.data; // { success, items, dimensions, limits }
    } catch (e) { _err(e); throw e; }
};

export const APIAdsAdjustmentCreate = async (token, campaignId, data) => {
    try {
        const r = await axios.post(
            `${ADS_API_BASE}/campaigns/${campaignId}/adjustments/create/`, data, auth(token)
        );
        return r.data;
    } catch (e) { _err(e); throw e; }
};

export const APIAdsAdjustmentUpdate = async (token, adjustmentId, data) => {
    try {
        const r = await axios.patch(
            `${ADS_API_BASE}/adjustments/${adjustmentId}/`, data, auth(token)
        );
        return r.data;
    } catch (e) { _err(e); throw e; }
};

export const APIAdsAdjustmentDelete = async (token, adjustmentId) => {
    try {
        const r = await axios.delete(`${ADS_API_BASE}/adjustments/${adjustmentId}/`, auth(token));
        return r.data;
    } catch (e) { _err(e); throw e; }
};


// ===================== РОТАЦИЯ =====================

export const APIAdsGroupRotation = async (token, groupId, rotationMode) => {
    try {
        const r = await axios.patch(
            `${ADS_API_BASE}/groups/${groupId}/rotation/`,
            { rotation_mode: rotationMode },
            auth(token),
        );
        return r.data;
    } catch (e) { _err(e); throw e; }
};


// ===================== АВТОСТРАТЕГИЯ =====================

export const APIAdsStrategyState = async (token, campaignId) => {
    try {
        const r = await axios.get(
            `${ADS_API_BASE}/campaigns/${campaignId}/strategy/`, auth(token)
        );
        return r.data; // { success, is_auto, state, history }
    } catch (e) { _err(e); throw e; }
};

export const APIAdsStrategyRecalc = async (token, campaignId) => {
    try {
        const r = await axios.post(
            `${ADS_API_BASE}/campaigns/${campaignId}/strategy/recalc/`, {}, auth(token)
        );
        return r.data;
    } catch (e) { _err(e); throw e; }
};


// ===================== АВТОПРАВИЛА =====================

export const APIAdsRules = async (token) => {
    try {
        const r = await axios.get(`${ADS_API_BASE}/rules/`, auth(token));
        return r.data; // { success, items, metrics, operators, actions }
    } catch (e) { _err(e); throw e; }
};

export const APIAdsRuleCreate = async (token, data) => {
    try {
        const r = await axios.post(`${ADS_API_BASE}/rules/create/`, data, auth(token));
        return r.data;
    } catch (e) { _err(e); throw e; }
};

export const APIAdsRuleUpdate = async (token, ruleId, data) => {
    try {
        const r = await axios.patch(`${ADS_API_BASE}/rules/${ruleId}/`, data, auth(token));
        return r.data;
    } catch (e) { _err(e); throw e; }
};

export const APIAdsRuleDelete = async (token, ruleId) => {
    try {
        const r = await axios.delete(`${ADS_API_BASE}/rules/${ruleId}/`, auth(token));
        return r.data;
    } catch (e) { _err(e); throw e; }
};

export const APIAdsRuleLogs = async (token, { ruleId, page = 1, pageSize = 30 } = {}) => {
    try {
        const params = new URLSearchParams();
        if (ruleId) params.append('rule_id', ruleId);
        params.append('page', page);
        params.append('page_size', pageSize);
        const r = await axios.get(`${ADS_API_BASE}/rules/logs/?${params}`, auth(token));
        return r.data;
    } catch (e) { _err(e); throw e; }
};


// ===================== ПРОГНОЗАТОР =====================

export const APIAdsForecast = async (token, {
    placements, pricingModel, bid, dailyBudget, targeting,
}) => {
    try {
        const r = await axios.post(`${ADS_API_BASE}/forecast/`, {
            placements,
            pricing_model: pricingModel,
            bid,
            daily_budget: dailyBudget,
            targeting,
        }, auth(token));
        return r.data;
    } catch (e) { _err(e); throw e; }
};


// ===================== ОЦЕНКА ОХВАТА =====================

/**
 * Сколько профилей попадает под таргетинг. Эндпоинт из 5-го этапа бэкенда,
 * используем досрочно: без него аудитория настраивается вслепую.
 */
export const APIAdsReach = async (token, targeting) => {
    try {
        const r = await axios.post(
            `${ADS_API_BASE}/forecast/reach/`, { targeting }, auth(token)
        );
        return r.data; // { success, reach }
    } catch (e) { _err(e); throw e; }
};


// ===================== ЁМКОСТЬ / ПЕРЕПОЛНЕНИЕ =====================

export const APIAdsCapacity = async (token, { city = null, region = null } = {}) => {
    try {
        const r = await axios.post(`${ADS_API_BASE}/capacity/`, { city, region }, auth(token));
        return r.data; // { success, city, region, scope?, capacity, slots, note? }
    } catch (e) { _err(e); throw e; }
};

export const APIAdsOversell = async (token, campaignId) => {
    try {
        const r = await axios.get(`${ADS_API_BASE}/campaigns/${campaignId}/oversell/`, auth(token));
        return r.data; // { success, oversell: { status, message, demand?, city?, region?, ...capacity } }
    } catch (e) { _err(e); throw e; }
};

// ===================== УВЕДОМЛЕНИЯ КАБИНЕТА =====================

export const APIAdsNotifications = async (token, { onlyUnread = false, page = 1, pageSize = 20 } = {}) => {
    try {
        const params = new URLSearchParams();
        if (onlyUnread) params.append('only_unread', 'true');
        params.append('page', page);
        params.append('page_size', pageSize);
        const r = await axios.get(`${ADS_API_BASE}/notifications/?${params.toString()}`, auth(token));
        return r.data; // { success, unread, items, total, page, pages }
    } catch (e) { _err(e); throw e; }
};

export const APIAdsNotificationsRead = async (token, { ids = null, all = false } = {}) => {
    try {
        const body = all ? { all: true } : { ids: ids || [] };
        const r = await axios.post(`${ADS_API_BASE}/notifications/read/`, body, auth(token));
        return r.data; // { success, updated }
    } catch (e) { _err(e); throw e; }
};